import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { classifyAuthError } from '../../lib/auth/authThrottle';

interface GoogleSignInButtonProps {
  /** Texte d'aide sous le bouton ; masque si absent. */
  hint?: string;
}

/** Logo « G » officiel, en couleurs, comme l'exigent les consignes de Google. */
const GoogleLogo = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const GoogleSignInButton = ({ hint }: GoogleSignInButtonProps) => {
  const signInWithGoogle = useAuthStore((state) => state.signInWithGoogle);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onClick = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    setError(null);
    const { error } = await signInWithGoogle();
    // En cas de succes, le navigateur quitte la page : on ne revient ici
    // qu'en cas d'echec.
    if (error) {
      setIsRedirecting(false);
      setError(
        classifyAuthError(error) === 'network'
          ? 'Impossible de joindre le serveur. Vérifiez votre connexion internet et réessayez.'
          : 'La connexion avec Google a échoué. Réessayez ou utilisez votre email et mot de passe.',
      );
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={isRedirecting}
        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-md bg-white text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {isRedirecting ? <Loader2 className="animate-spin h-5 w-5" /> : <GoogleLogo />}
        Continuer avec Google
      </button>
      {hint && <p className="mt-2 text-xs text-gray-500 text-center">{hint}</p>}
      {error && (
        <div className="mt-2 p-3 bg-red-50 text-red-600 rounded-md text-sm" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default GoogleSignInButton;
